import clsx from 'clsx'
import { Icon } from '@iconify/react'
import { Formik, Form, Field, FieldArray, FieldProps } from 'formik'

import { Button, Input, useMessage } from '@pepper/ui'
import Footer from 'components/idea-lab/ImportModalFooter'

import { useCreateBulkKeywords } from 'utils/hooks/useCreateBulkKeywords'
import validator, { getErrorMsgs } from './validator'
import { getBulkImportMsg } from '../../../utils'

import { type FormValues } from './types'

import s from './ManualForm.module.css'

const MAX_KEYWORDS_LENGTH = 10

type Props = {
  companyId: string
  projectId: string
  onBack: () => void
  onClose: () => void
  onChange: () => void
  setIsAddingBulkKeywords: (value: boolean) => void
  showFooter: boolean
}

export const ManualForm: React.FC<Props> = ({
  onBack,
  onClose,
  onChange,
  companyId,
  projectId,
  showFooter,
  setIsAddingBulkKeywords,
}) => {
  const { showError, showSuccess, showWarning } = useMessage()
  const { mutateAsync: addBulkKeywords, isLoading: isBulkAdditionLoading } =
    useCreateBulkKeywords()

  const initialValues = {
    keywords: ['', '', ''],
  }

  const handleBulkAddKeywords = async (values: FormValues) => {
    setIsAddingBulkKeywords(true)
    const filteredKeywords = values.keywords
      .filter(keyword => keyword !== '')
      .map(keyword => keyword.trim())

    const response = await addBulkKeywords({
      companyId,
      projectId,
      keywords: filteredKeywords,
    })

    setIsAddingBulkKeywords(false)
    if (response?.error) {
      showError(response.error)
    } else {
      const totalAdded = response?.records?.length || 0
      const totalSkipped = filteredKeywords.length - totalAdded
      const msg = getBulkImportMsg({
        type: 'Keyword',
        totalAdded,
        totalSkipped,
      })

      if (totalAdded === 0) {
        showWarning(msg)
      } else {
        showSuccess(msg)
      }
      onClose()
    }
  }

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    onSetValue: (keywords: FormValues['keywords']) => void
  ) => {
    event.preventDefault()
    if (event.clipboardData === null) return
    const text = event.clipboardData.getData('text')
    const pastedKeywords = text.split('\n')

    const filteredKeywords = pastedKeywords.filter(keyword => keyword !== '')

    if (filteredKeywords.length > 0) {
      onSetValue(filteredKeywords.slice(0, MAX_KEYWORDS_LENGTH))
      onChange()
    }
  }

  return (
    <div className={s.wrapper}>
      <p className={s.description}>
        Enter your keywords manually. You can also add multiple keywords at
        once.
      </p>
      <Formik
        onSubmit={handleBulkAddKeywords}
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        validate={validator}
        render={({ values, errors, resetForm, setFieldValue, dirty }) => (
          <Form className={s.form}>
            <FieldArray
              name="keywords"
              render={arrayHelpers => {
                return (
                  <>
                    <div className={s.rows}>
                      {values.keywords.map((_, index) => (
                        <div key={index} className={s.row}>
                          <Field name={`keywords.${index}`}>
                            {({ field, meta }: FieldProps) => (
                              <Input
                                {...field}
                                onChange={e => {
                                  field.onChange(e)
                                  e.target.value !== '' && onChange()
                                }}
                                onPaste={e =>
                                  !dirty &&
                                  handlePaste(e, keywords =>
                                    setFieldValue('keywords', keywords)
                                  )
                                }
                                className={clsx(s.input, {
                                  [s.error]: meta.error,
                                })}
                                placeholder={`Keyword ${index + 1}`}
                              />
                            )}
                          </Field>
                          <Button
                            className={s.deleteBtn}
                            type="text"
                            htmlType="button"
                            icon={
                              <Icon
                                icon="lucide:trash-2"
                                width={12}
                                height={12}
                              />
                            }
                            onClick={
                              values.keywords.length > 1
                                ? () => arrayHelpers.remove(index)
                                : undefined
                            }
                          />
                        </div>
                      ))}
                    </div>
                    {errors.keywords &&
                      getErrorMsgs(errors.keywords).map(error => (
                        <div key={error} className={s.errorMsg}>
                          <Icon icon="lucide:info" />
                          {error}
                        </div>
                      ))}

                    <Button
                      className={s.addRowBtn}
                      icon={<Icon icon="lucide:plus" />}
                      type="link"
                      htmlType="button"
                      onClick={() => arrayHelpers.push('')}
                      disabled={values.keywords.length >= MAX_KEYWORDS_LENGTH}
                      {...(values.keywords.length >= MAX_KEYWORDS_LENGTH && {
                        title: `You can only add ${MAX_KEYWORDS_LENGTH} topics`,
                      })}
                    >
                      Add another row
                    </Button>
                  </>
                )
              }}
            />
            {showFooter && (
              <Footer
                disabled={values.keywords.length === 0}
                loading={isBulkAdditionLoading}
                onBack={() => {
                  onBack()
                  resetForm()
                }}
                onCancel={onClose}
                okText="Add Keywords"
              />
            )}
          </Form>
        )}
      />
    </div>
  )
}
